"use client";

import type { SimpleTableConfig, BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import {
  normalizeSerializedFilters as normalizeFilters,
  convertToGraphQLFilter as convertFilters,
} from "@/lib/services/filter-conversion";

/**
 * Re-export filter conversion utilities
 */
export const normalizeSerializedFilters = normalizeFilters;
export const convertToGraphQLFilter = convertFilters;

/**
 * Helper function to construct search filter
 * For deals entity, uses full-text search via RPC function
 * For other entities, falls back to ilike on text fields
 */
export function constructSearchFilter(
  searchTerm: string,
  // SimpleTableConfig is invariant in T; we only read `fields` (covariant), so SimpleTableConfigBase works here.
  config: Pick<SimpleTableConfig<BaseEntity>, "fields">,
  entityName?: string
): Record<string, unknown> {
  if (!searchTerm || !config.fields) {
    return {};
  }

  // For deals, use full-text search via RPC function
  // This is handled at the query level in useUniversalEntityPageData
  // Return a marker that indicates full-text search should be used
  if (entityName === "deals") {
    return {
      _fullTextSearch: searchTerm,
    };
  }

  const searchableFields = Object.entries(config.fields)
    .filter(([, fieldConfig]) => fieldConfig.searchable !== false)
    .map(([fieldId]) => fieldId);

  if (searchableFields.length === 0) {
    return {};
  }

  const textFields: string[] = [];
  const enumFields: string[] = [];
  const uuidFields: string[] = [];
  const otherFields: string[] = [];

  searchableFields.forEach((fieldId) => {
    const fieldConfig = config.fields[fieldId];

    // Check if this is a UUID field first (regardless of type in config)
    if (
      fieldConfig.type === "client-select" ||
      fieldId.endsWith("_id") ||
      fieldId === "assigned_to" ||
      fieldId === "created_by" ||
      fieldId === "updated_by" ||
      fieldId === "company_id"
    ) {
      // UUID fields that should not use ilike
      uuidFields.push(fieldId);
    } else if (fieldConfig.type === "select" || fieldConfig.type === "boolean") {
      enumFields.push(fieldId);
    } else if (fieldConfig.type === "number") {
      // Number fields should not use ilike
      otherFields.push(fieldId);
    } else if (fieldConfig.type === "text" || fieldConfig.type === "textarea") {
      textFields.push(fieldId);
    } else if (fieldConfig.type === "date" || fieldConfig.type === "datetime") {
      otherFields.push(fieldId);
    } else {
      otherFields.push(fieldId);
    }
  });

  const priorityFields = [
    "name",
    "company_name",
    "email",
    "title",
    "phone",
    "address",
    "city",
    "state",
    "zip_code",
    "country",
    "website",
    "payment_terms",
    "tax_id",
    "notes",
  ];
  const orderedTextFields = [
    ...priorityFields.filter((field) => textFields.includes(field)),
    ...textFields.filter((field) => !priorityFields.includes(field)),
  ];

  const orConditions: Array<Record<string, { ilike: string }>> = [];

  // Only apply ilike to actual text fields, not UUID fields
  orderedTextFields.forEach((field) => {
    orConditions.push({
      [field]: { ilike: `%${searchTerm}%` },
    });
  });

  if (orConditions.length === 0) {
    return {};
  }

  if (orConditions.length === 1) {
    return orConditions[0];
  } else {
    return {
      or: orConditions,
    };
  }
}

/** Scalar or array filter value used in GraphQL operators. */
type GraphQLFilterValue = unknown;

/** Range filter value with optional min/max bounds. */
interface RangeFilterValue {
  min?: unknown;
  max?: unknown;
}

/**
 * Maps UI filter types to GraphQL operator and value transform
 */
function mapOperatorToGraphQL(
  operator: string,
  value: GraphQLFilterValue
): { operator: string; value: GraphQLFilterValue } | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) && value.length === 0) return null;

  switch (operator) {
    case "notNull":
      return { operator: "is", value: "NOT_NULL" };
    case "eq":
    case "exact":
      return Array.isArray(value) ? { operator: "in", value } : { operator: "eq", value };
    case "neq":
      return { operator: "neq", value };
    case "gt":
      return { operator: "gt", value };
    case "gte":
      return { operator: "gte", value };
    case "lt":
      return { operator: "lt", value };
    case "lte":
      return { operator: "lte", value };
    case "ilike":
      return { operator: "ilike", value: `%${String(value)}%` };
    case "startsWith":
      return { operator: "ilike", value: `${String(value)}%` };
    case "endsWith":
      return { operator: "ilike", value: `%${String(value)}` };
    case "like":
      return { operator: "ilike", value };
    case "in":
    case "multiSelect":
      return Array.isArray(value) ? { operator: "in", value } : { operator: "eq", value };
    case "overlaps":
      return Array.isArray(value) ? { operator: "overlaps", value } : null;
    case "contains":
      if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        return { operator: "contains", value };
      }
      return { operator: "ilike", value: `%${String(value)}%` };
    case "company-select":
    case "client-select":
    case "user-select":
      return { operator: "eq", value };
    case "numberRange":
    case "dateRange":
    case "timeRange": {
      const rangeValue = value as RangeFilterValue;
      if (rangeValue?.min != null || rangeValue?.max != null) {
        const rangeFilter: Record<string, unknown> = {};
        if (rangeValue.min != null) rangeFilter.gte = rangeValue.min;
        if (rangeValue.max != null) rangeFilter.lte = rangeValue.max;
        return { operator: "_range", value: rangeFilter };
      }
      return null;
    }
    default:
      return Array.isArray(value)
        ? { operator: "in", value }
        : { operator: "eq", value };
  }
}

/** Raw filter item from array-format advanced filters. */
interface RawFilterItem {
  id: string;
  field: string;
  operator: string;
  type?: string;
  value: GraphQLFilterValue;
}

/** Raw filter condition from Record-format advanced filters. */
interface RawFilterCondition {
  type?: string;
  operator?: string;
  value?: GraphQLFilterValue;
}

/** Normalized intermediate representation for building GraphQL filter. */
interface NormalizedFilterItem {
  fieldId: string;
  operator: string;
  value: GraphQLFilterValue;
}

/**
 * Normalize advanced filters to array format for processing
 */
function normalizeAdvancedFiltersInput(
  advancedFilters: Record<string, unknown> | RawFilterItem[]
): NormalizedFilterItem[] {
  if (Array.isArray(advancedFilters)) {
    return advancedFilters.map((f) => ({
      fieldId: f.field || f.id,
      operator: f.operator || f.type || "eq",
      value: f.value,
    }));
  }
  return Object.entries(advancedFilters).map(([fieldId, filter]) => {
    const condition = filter as RawFilterCondition | null | undefined;
    return {
      fieldId,
      operator: condition?.type ?? condition?.operator ?? "eq",
      value: condition?.value,
    };
  });
}

/**
 * Helper function to convert advanced filters to GraphQL "where" format
 * Supports both array (AdvancedFilterGroup[]) and Record formats
 * Maps operators: eq, neq, gt, gte, lt, lte, ilike, contains, overlaps, in, is
 */
export function convertAdvancedFiltersToGraphQL(
  advancedFilters: Record<string, unknown> | RawFilterItem[]
): Record<string, unknown> {
  const graphqlFilter: Record<string, unknown> = {};
  const items = normalizeAdvancedFiltersInput(advancedFilters);

  items.forEach(({ fieldId, operator, value }) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value) && value.length === 0 && operator !== "in" && operator !== "overlaps")
      return;

    const graphqlFieldName = fieldId.replace("filter_", "");
    const mapped = mapOperatorToGraphQL(operator, value);
    if (!mapped) return;

    if (mapped.operator === "_range") {
      graphqlFilter[graphqlFieldName] = mapped.value;
    } else if (mapped.operator === "is") {
      graphqlFilter[graphqlFieldName] = { is: mapped.value };
    } else {
      graphqlFilter[graphqlFieldName] = { [mapped.operator]: mapped.value };
    }
  });

  return graphqlFilter;
}
